import type { LngLat, MapCoords, PixelPoint } from '../model/types';

export const WORLD_TILE_SIZE_PX = 256;
export const MAX_MERCATOR_LATITUDE = 85.08405905011041;

const WGS84_ECCENTRICITY = 0.0818191908426215;
const INVERSE_ITERATIONS_LIMIT = 15;
const INVERSE_PRECISION = 1e-12;

const MAX_LONGITUDE = 180;
const DEGREES_IN_HALF_TURN = 180;
const DEGREES_IN_TURN = 360;

function clampLatitude(latitude: number): number {
  return Math.min(Math.max(latitude, -MAX_MERCATOR_LATITUDE), MAX_MERCATOR_LATITUDE);
}

function toRadians(degrees: number): number {
  return (degrees * Math.PI) / DEGREES_IN_HALF_TURN;
}

function toDegrees(radians: number): number {
  return (radians * DEGREES_IN_HALF_TURN) / Math.PI;
}

export function getWorldSizePx(zoom: number): number {
  return WORLD_TILE_SIZE_PX * 2 ** zoom;
}

function toMercatorY(phi: number): number {
  return (
    Math.log(Math.tan(Math.PI / 4 + phi / 2)) - WGS84_ECCENTRICITY * Math.atanh(WGS84_ECCENTRICITY * Math.sin(phi))
  );
}

function fromMercatorY(mercatorY: number): number {
  const t = Math.exp(-mercatorY);
  let phi = Math.PI / 2 - 2 * Math.atan(t);

  for (let iteration = 0; iteration < INVERSE_ITERATIONS_LIMIT; iteration += 1) {
    const eSin = WGS84_ECCENTRICITY * Math.sin(phi);
    const next = Math.PI / 2 - 2 * Math.atan(t * ((1 - eSin) / (1 + eSin)) ** (WGS84_ECCENTRICITY / 2));

    if (Math.abs(next - phi) < INVERSE_PRECISION) return next;

    phi = next;
  }

  return phi;
}

function toWorldFractionX(longitude: number): number {
  const fraction = (longitude + MAX_LONGITUDE) / DEGREES_IN_TURN;

  return fraction - Math.floor(fraction);
}

export function toPixelPoint(coords: MapCoords, zoom: number): PixelPoint {
  const worldSize = getWorldSizePx(zoom);
  const phi = toRadians(clampLatitude(coords.latitude));

  return {
    x: toWorldFractionX(coords.longitude) * worldSize,
    y: (0.5 - toMercatorY(phi) / (2 * Math.PI)) * worldSize,
  };
}

export function fromPixelPoint(point: PixelPoint, zoom: number): MapCoords {
  const worldSize = getWorldSizePx(zoom);
  const phi = fromMercatorY((0.5 - point.y / worldSize) * 2 * Math.PI);

  return {
    latitude: toDegrees(phi),
    longitude: (point.x / worldSize) * DEGREES_IN_TURN - MAX_LONGITUDE,
  };
}

export function isValidCoords(coords: MapCoords | null | undefined): boolean {
  if (!coords) return false;

  const { latitude, longitude } = coords;

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return false;
  if (Math.abs(latitude) > MAX_MERCATOR_LATITUDE || Math.abs(longitude) > MAX_LONGITUDE) return false;

  return latitude !== 0 || longitude !== 0;
}

export function toLngLat(coords: MapCoords): LngLat {
  return [coords.longitude, coords.latitude];
}
